import React from "react";
import { canEditAgent } from "../../../../utils/agentRbac";
import { COLORS } from "../../../../theme/colors";
import { MATERIALS } from "../../../../theme/materials";
import AgentModelField from "./AgentModelField";
import { platformManagedKind, platformManagedDescription } from "./platformManagedAgent";
import AgentAvatar from "../../iphone-grid/AgentAvatar";
import AvatarPicker from "../../iphone-grid/AvatarPicker";

// Locked-down Detail Viewer for platform-managed agents
// (is_super_agent / is_platform_tester / is_self_assembling).
// Only provider + model are editable; everything else is hidden
// because the system prompt, toolsets, flow_config etc. are
// platform IP we don't expose to tenants.

export default function PlatformManagedView({
  tree, saveAgent, onBack, currentUser,
  isDirty, dirtyCount, onCommit, onDiscard, committing,
}) {
  const agent = tree?.agent || {};
  const kindLabel = platformManagedKind(agent);
  const description = platformManagedDescription(agent);
  const canEdit = canEditAgent(currentUser?.role);
  const [avatarPickerOpen, setAvatarPickerOpen] = React.useState(false);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Header */}
      <div style={{
        ...MATERIALS.thickGlass(),
        borderRadius: 0,
        borderBottom: `1px solid ${COLORS.border}`,
        borderTop: "none", borderLeft: "none", borderRight: "none",
        padding: "16px 24px",
        display: "flex", alignItems: "center", gap: 16,
      }}>
        <button onClick={onBack} style={{
          background: "transparent", border: `1px solid ${COLORS.border}`,
          color: COLORS.textDim, padding: "6px 12px", borderRadius: 8,
          cursor: "pointer", fontSize: 12,
        }}>{"← Back"}</button>

        {/* Clickable avatar — change-color/icon entry. business_user
            keeps the avatar but non-clickable. ✎ overlay on hover. */}
        {agent && (
          <button
            type="button"
            onClick={() => canEdit && setAvatarPickerOpen(true)}
            disabled={!canEdit}
            title={canEdit ? "Change avatar" : agent.name}
            style={{
              position: "relative",
              background: "transparent", border: "none", padding: 0,
              cursor: canEdit ? "pointer" : "default",
              borderRadius: 14, display: "flex",
              transition: "transform 0.15s",
            }}
            onMouseEnter={(e) => {
              if (!canEdit) return;
              e.currentTarget.style.transform = "scale(1.05)";
              const overlay = e.currentTarget.querySelector("[data-edit-overlay]");
              if (overlay) overlay.style.opacity = "1";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
              const overlay = e.currentTarget.querySelector("[data-edit-overlay]");
              if (overlay) overlay.style.opacity = "0";
            }}
          >
            <AgentAvatar agent={agent} size={44} variant="tile" />
            {canEdit && (
              <span
                data-edit-overlay
                style={{
                  position: "absolute", inset: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  background: "rgba(0,0,0,0.45)", color: "#fff",
                  fontSize: 14, fontWeight: 700, borderRadius: 14,
                  opacity: 0, transition: "opacity 0.15s",
                  pointerEvents: "none",
                }}
              >✎</span>
            )}
          </button>
        )}

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: COLORS.text }}>{agent.name}</div>
          {kindLabel && (
            <div style={{ fontSize: 11, color: COLORS.cyan || "#06B6D4", marginTop: 2, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase" }}>
              {kindLabel}
            </div>
          )}
        </div>
        {isDirty && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{
              padding: "3px 10px", borderRadius: 999, fontSize: 11,
              background: `${COLORS.orange}22`, color: COLORS.orange,
              border: `1px solid ${COLORS.orange}55`, fontWeight: 600,
            }}>{`${dirtyCount} unsaved`}</span>
            <button onClick={onDiscard} disabled={committing}
              style={{
                background: "transparent", border: `1px solid ${COLORS.border}`,
                color: COLORS.textDim, padding: "6px 12px", borderRadius: 8,
                cursor: committing ? "default" : "pointer", fontSize: 11, fontWeight: 600,
                opacity: committing ? 0.55 : 1,
              }}>Discard</button>
            <button onClick={onCommit} disabled={committing}
              style={{
                background: COLORS.accent, border: "none",
                color: "#fff", padding: "6px 14px", borderRadius: 8,
                cursor: committing ? "default" : "pointer", fontSize: 11, fontWeight: 600,
                opacity: committing ? 0.55 : 1,
              }}>{committing ? "Saving…" : "Save changes"}</button>
          </div>
        )}
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflow: "auto", padding: 24, maxWidth: 720, margin: "0 auto", width: "100%" }}>
        {/* Description card — read-only, explains what this agent is */}
        <div style={{
          padding: 16, borderRadius: 12,
          border: `1px solid ${COLORS.border}`,
          background: COLORS.surface,
          boxShadow: MATERIALS.spatialShadow(),
          marginBottom: 16,
        }}>
          <div style={{
            fontSize: 11, fontWeight: 700, color: COLORS.textDim,
            textTransform: "uppercase", letterSpacing: "0.06em",
            marginBottom: 10,
          }}>About this agent</div>
          <div style={{ fontSize: 13, color: COLORS.text, lineHeight: 1.6 }}>
            {description}
          </div>
          <div style={{
            marginTop: 12, padding: "10px 12px", borderRadius: 6,
            background: `${COLORS.cyan || "#06B6D4"}15`,
            border: `1px solid ${COLORS.cyan || "#06B6D4"}40`,
            fontSize: 11, color: COLORS.text, lineHeight: 1.5,
          }}>
            <strong style={{ color: COLORS.cyan || "#06B6D4" }}>Platform-managed.</strong>
            {" "}This agent's logic, prompts, and tools are maintained by the platform. You bring the LLM provider + model; everything else is taken care of.
          </div>
        </div>

        {/* LLM card — the ONLY editable surface */}
        <div style={{
          padding: 16, borderRadius: 12,
          border: `1px solid ${COLORS.border}`,
          background: COLORS.surface,
          boxShadow: MATERIALS.spatialShadow(),
        }}>
          <div style={{
            fontSize: 11, fontWeight: 700, color: COLORS.textDim,
            textTransform: "uppercase", letterSpacing: "0.06em",
            marginBottom: 10,
          }}>LLM</div>
          {/* Phase M (M4-2): the shared picker — workspace default or a pinned model. */}
          <AgentModelField agent={agent} saveAgent={saveAgent} readOnly={!canEdit} />
        </div>

        {/* Footer note — explicitly tell power users where the rest lives */}
        <div style={{
          marginTop: 16, padding: 14, borderRadius: 10,
          background: COLORS.surface,
          border: `1px solid ${COLORS.border}`,
          fontSize: 11, color: COLORS.textMuted, lineHeight: 1.5,
        }}>
          To tune the agents this assistant <em>creates</em> (not the assistant itself),
          open the agent it creates and use the Detail Viewer there.
        </div>
      </div>

      {avatarPickerOpen && (
        <AvatarPicker
          agent={agent}
          value={agent.metadata_json?.icon}
          onChange={(v) => saveAgent({ metadata_json: { icon: v } })}
          onClose={() => setAvatarPickerOpen(false)}
        />
      )}
    </div>
  );
}
